// src/features/mensagens/api/mensagensService.js
// ============================================================================
// 🗄️ MENSAGENS SERVICE — ÚNICO PUNTO DE ACCESO A DATOS
// 📦 V2 ENTERPRISE — Singleton con userId · Multi-Backend · Offline-First
// ============================================================================
//
// REGLAS DE ORO:
// 1. TODO acceso a datos de mensagens pasa por ESTE archivo
// 2. NINGÚN componente importa DataContext, Firestore, o IndexedDB directamente
// 3. Si mañana cambia el backend, SOLO se modifica este archivo
// 4. Compatible 100% con el sistema actual (DataContext + database.js)
// 5. V2: userId requerido — getInstance(userId) o setUserId() antes de usar
// ============================================================================

import DataContext from '../../../lib/data/context/DataContext';

class MensagensService {
  constructor() {
    this._ctx = null;
    this._ready = false;
    this._userId = null;
    this._initializing = false;
    this._initPromise = null;
  }

  // ==========================================================================
  // 🔐 GESTIÓN DE IDENTIDAD (V2 — Ultra Robusto)
  // ==========================================================================

  /**
   * Establece el userId para esta instancia.
   * DEBE llamarse antes de cualquier operación de datos.
   * @param {string} userId - ID del usuario autenticado
   */
  setUserId(userId) {
    if (!userId) {
      console.warn('⚠️ [MensagensService] setUserId llamado sin userId');
      return;
    }

    // Si ya tenemos el mismo userId, no reiniciar
    if (this._userId === userId && this._ready) {
      return;
    }

    // Si cambia el userId, resetear contexto
    if (this._userId && this._userId !== userId) {
      console.log('🔄 [MensagensService] Cambio de usuario detectado, reiniciando contexto');
      this._ctx = null;
      this._ready = false;
      this._initPromise = null;
    }

    this._userId = userId;
  }

  /**
   * Obtiene el userId actual, con fallback a localStorage
   */
  _resolveUserId() {
    if (this._userId) return this._userId;

    // Fallback 1: localStorage auth_user
    try {
      const stored = localStorage.getItem('auth_user');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.id) {
          this._userId = parsed.id;
          console.log('👤 [MensagensService] userId resuelto de localStorage:', this._userId);
          return this._userId;
        }
      }
    } catch {}

    // Fallback 2: localStorage imoveispro_db
    try {
      const db = JSON.parse(localStorage.getItem('imoveispro_db') || '{}');
      const users = db.users || [];
      if (users.length > 0 && users[0]?.id) {
        this._userId = users[0].id;
        console.log('👤 [MensagensService] userId resuelto de imoveispro_db:', this._userId);
        return this._userId;
      }
    } catch {}

    // Fallback 3: Firebase Auth (si está disponible en window)
    try {
      if (window.firebase?.auth?.currentUser?.uid) {
        this._userId = window.firebase.auth.currentUser.uid;
        console.log('👤 [MensagensService] userId resuelto de Firebase Auth:', this._userId);
        return this._userId;
      }
    } catch {}

    console.error('❌ [MensagensService] No se pudo resolver userId. Asegúrate de llamar setUserId()');
    return null;
  }

  // ==========================================================================
  // 📦 CONTEXTO DE DATOS
  // ==========================================================================

  async _getContext() {
    // Si ya está listo, devolver inmediatamente
    if (this._ctx && this._ready) return this._ctx;

    // Si ya se está inicializando, esperar a que termine
    if (this._initializing && this._initPromise) {
      return this._initPromise;
    }

    this._initializing = true;

    this._initPromise = (async () => {
      try {
        const userId = this._resolveUserId();

        if (!userId) {
          throw new Error('userId no disponible. Llama a setUserId() primero.');
        }

        console.log('🔌 [MensagensService] Conectando DataContext para usuario:', userId);
        this._ctx = await DataContext.getInstance(userId);
        this._ready = true;
        this._initializing = false;
        console.log('✅ [MensagensService] DataContext inicializado correctamente');
        return this._ctx;
      } catch (error) {
        this._initializing = false;
        this._initPromise = null;
        console.error('❌ [MensagensService] Error al obtener DataContext:', error);
        throw new Error('No se pudo conectar con la base de datos: ' + error.message);
      }
    })();

    return this._initPromise;
  }

  /**
   * Verifica si el servicio está listo para operar
   */
  async isReady() {
    try {
      await this._getContext();
      return this._ready;
    } catch {
      return false;
    }
  }

  /**
   * Resetea el contexto (útil para cambios de usuario o logout)
   */
  reset() {
    this._ctx = null;
    this._ready = false;
    this._userId = null;
    this._initPromise = null;
    this._initializing = false;
    console.log('🔄 [MensagensService] Contexto reseteado');
  }

  // --------------------------------------------------------------------------
  // 📥 LECTURA
  // --------------------------------------------------------------------------

  async getMessages() {
    try {
      const ctx = await this._getContext();
      try {
        if (ctx?.collections?.messages?.read) {
          const messages = await ctx.collections.messages.read();
          if (Array.isArray(messages) && messages.length >= 0) return messages;
        }
      } catch (err) {
        console.warn('⚠️ [MensagensService] Error leyendo mensajes:', err.message);
      }
      const database = ctx?._database || ctx?.database;
      if (database?.getMessages) {
        const msgs = await database.getMessages();
        if (Array.isArray(msgs)) return msgs;
      }
    } catch (e) {
      console.warn('⚠️ [MensagensService] getMessages contexto falló, fallback local:', e.message);
    }
    // Fallback localStorage (API simulada offline)
    try {
      const uid = this._resolveUserId() || 'anon';
      const key = `imoveispro_db_user_${uid}`;
      const db = JSON.parse(localStorage.getItem(key) || '{}');
      return Array.isArray(db.messages) ? db.messages : [];
    } catch {
      return [];
    }
  }

  async getMessageById(messageId) {
    if (!messageId) return null;
    const ctx = await this._getContext();
    try {
      const database = ctx?._database || ctx?.database;
      if (database?.getMessage) return await database.getMessage(messageId);
    } catch (err) {
      console.warn('⚠️ [MensagensService] Error obteniendo mensaje:', err.message);
    }
    const messages = await this.getMessages();
    return messages.find(m => m.id === messageId) || null;
  }

  async getMessagesByClient() {
    const messages = await this.getMessages();
    const byClient = {};
    for (const msg of messages) {
      const clientId = msg.clientId || 'unknown';
      if (!byClient[clientId]) byClient[clientId] = [];
      byClient[clientId].push(msg);
    }
    for (const clientId of Object.keys(byClient)) {
      byClient[clientId].sort((a, b) => (b?.createdAt || 0) - (a?.createdAt || 0));
    }
    return byClient;
  }

  async getMessagesByClientId(clientId) {
    if (!clientId) return [];
    const byClient = await this.getMessagesByClient();
    return byClient[clientId] || [];
  }

  async getStats() {
    const ctx = await this._getContext();
    try {
      const database = ctx?._database || ctx?.database;
      if (database?.getMessagesStats) return await database.getMessagesStats();
    } catch (err) {
      console.warn('⚠️ [MensagensService] Error obteniendo stats:', err.message);
    }
    const messages = await this.getMessages();
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;
    return {
      total: messages.length,
      today: messages.filter(m => m.createdAt > now - oneDay).length,
      thisWeek: messages.filter(m => m.createdAt > now - 7 * oneDay).length,
      thisMonth: messages.filter(m => m.createdAt > now - 30 * oneDay).length,
      sent: messages.filter(m => m.status === 'sent').length,
      delivered: messages.filter(m => m.status === 'delivered').length,
      read: messages.filter(m => m.status === 'read').length,
      failed: messages.filter(m => m.status === 'failed').length,
      uniqueClients: new Set(messages.map(m => m.clientId).filter(Boolean)).size,
    };
  }

  // --------------------------------------------------------------------------
  // 📤 ESCRITURA
  // --------------------------------------------------------------------------

  async sendMessage({ clientId, clientName, clientPhone, message, propertyId = null, template = null, attachments = undefined, status = 'sent' }) {
    if (!clientId || !message) throw new Error('clientId y message son obligatorios');
    const ctx = await this._getContext();
    const now = Date.now();
    // 🔥 ID estable tipo API real (offline-first)
    const id =
      (typeof crypto !== 'undefined' && crypto.randomUUID?.()) ||
      `msg_${now}_${Math.random().toString(36).slice(2, 9)}`;
    const msgData = {
      id,
      clientId,
      clientName: clientName || 'Desconhecido',
      clientPhone: clientPhone || '',
      message: String(message).trim(),
      content: { body: String(message).trim() }, // compat UI bubbles
      direction: 'outbound',
      status: status || 'sent',
      createdAt: now,
      updatedAt: now,
      propertyId: propertyId || null,
      template: template || null,
      attachments: attachments || undefined,
      readAt: null,
      edited: false,
      simulated: true, // sin API externa — comportamiento local real
    };
    try {
      if (ctx?.collections?.messages?.create) {
        const created = await ctx.collections.messages.create(msgData);
        return created?.id ? { ...msgData, ...created } : msgData;
      }
    } catch (err) {
      console.warn('⚠️ [MensagensService] Error creando mensaje:', err.message);
    }
    const database = ctx?._database || ctx?.database;
    if (database?.createMessage) {
      const created = await database.createMessage(msgData);
      return created?.id ? { ...msgData, ...created } : msgData;
    }
    // Fallback localStorage ultra-robusto (simula API)
    try {
      const uid = this._resolveUserId() || 'anon';
      const key = `imoveispro_db_user_${uid}`;
      const db = JSON.parse(localStorage.getItem(key) || '{}');
      db.messages = Array.isArray(db.messages) ? db.messages : [];
      db.messages.unshift(msgData);
      localStorage.setItem(key, JSON.stringify(db));
      window.dispatchEvent(new CustomEvent('mensagens:changed', { detail: { type: 'create', message: msgData } }));
      return msgData;
    } catch (e) {
      console.error('❌ [MensagensService] Fallback local falló:', e.message);
    }
    throw new Error('No se pudo enviar el mensaje');
  }

  async resendMessage(originalMessageId, { clientId, clientName, clientPhone }) {
    const original = await this.getMessageById(originalMessageId);
    if (!original) throw new Error('Mensaje original no encontrado');
    return this.sendMessage({
      clientId,
      clientName,
      clientPhone,
      message: original.message || '',
      propertyId: original.propertyId || null,
      template: original.template || null,
    });
  }

  async receiveMessage({ clientId, clientName, clientPhone, message }) {
    const ctx = await this._getContext();
    const now = Date.now();
    const msgData = {
      clientId,
      clientName: clientName || 'Desconhecido',
      clientPhone: clientPhone || '',
      message: message.trim(),
      direction: 'inbound',
      status: 'delivered',
      createdAt: now,
      updatedAt: now,
      readAt: null,
      edited: false,
    };
    try {
      if (ctx?.collections?.messages?.create) {
        await ctx.collections.messages.create(msgData);
        return msgData;
      }
    } catch (err) {
      console.warn('⚠️ [MensagensService] Error registrando recibido:', err.message);
    }
    const database = ctx?._database || ctx?.database;
    if (database?.createMessage) return await database.createMessage(msgData);
    throw new Error('No se pudo registrar el mensaje recibido');
  }

  // --------------------------------------------------------------------------
  // ✏️ ACTUALIZACIÓN
  // --------------------------------------------------------------------------

  async markAsRead(messageId) {
    if (!messageId) return;
    const ctx = await this._getContext();
    try {
      if (ctx?.collections?.messages?.update) {
        await ctx.collections.messages.update(messageId, { status: 'read', readAt: Date.now() });
        return;
      }
    } catch (err) {
      console.warn('⚠️ [MensagensService] Error marcando leído:', err.message);
    }
    const database = ctx?._database || ctx?.database;
    if (database?.updateMessage) await database.updateMessage(messageId, { status: 'read', readAt: Date.now() });
  }

  async markAllAsRead(clientId) {
    if (!clientId) return 0;
    const messages = await this.getMessagesByClientId(clientId);
    const unread = messages.filter(m => m.status !== 'read');
    for (const msg of unread) await this.markAsRead(msg.id);
    return unread.length;
  }

  async editMessage(messageId, newText) {
    if (!messageId || !newText) throw new Error('messageId y newText son obligatorios');
    const ctx = await this._getContext();
    try {
      if (ctx?.collections?.messages?.update) {
        await ctx.collections.messages.update(messageId, {
          message: newText.trim(), edited: true, updatedAt: Date.now(),
        });
        return;
      }
    } catch (err) {
      console.warn('⚠️ [MensagensService] Error editando:', err.message);
    }
    const database = ctx?._database || ctx?.database;
    if (database?.updateMessage) await database.updateMessage(messageId, {
      message: newText.trim(), edited: true, updatedAt: Date.now(),
    });
  }

  // --------------------------------------------------------------------------
  // 🗑️ ELIMINACIÓN
  // --------------------------------------------------------------------------

  async deleteMessage(messageId) {
    if (!messageId) return;
    const ctx = await this._getContext();
    try {
      if (ctx?.collections?.messages?.delete) {
        await ctx.collections.messages.delete(messageId);
        return;
      }
    } catch (err) {
      console.warn('⚠️ [MensagensService] Error eliminando:', err.message);
    }
    const database = ctx?._database || ctx?.database;
    if (database?.deleteMessage) await database.deleteMessage(messageId);
  }

  async deleteConversation(clientId) {
    if (!clientId) return 0;
    const messages = await this.getMessagesByClientId(clientId);
    for (const msg of messages) await this.deleteMessage(msg.id);
    return messages.length;
  }
}

// ============================================================================
// 🎯 SINGLETON ULTRA ROBUSTO V2
// ============================================================================

let instance = null;

/**
 * Obtiene la instancia única del servicio de mensajes.
 * 
 * @param {string} [userId] - ID del usuario autenticado (opcional pero recomendado).
 *   Si no se proporciona, se intenta resolver de localStorage o Firebase Auth.
 * @returns {MensagensService} Instancia singleton del servicio
 * 
 * @example
 *   // Con userId explícito (recomendado)
 *   const service = getMensagensService(user.uid);
 * 
 * @example
 *   // Sin userId — lo resuelve automáticamente de localStorage
 *   const service = getMensagensService();
 */
export function getMensagensService(userId) {
  if (!instance) {
    instance = new MensagensService();
  }

  // Si se proporciona userId, configurarlo
  if (userId) {
    instance.setUserId(userId);
  }

  return instance;
}

/**
 * Resetea el singleton (útil para logout o cambio de usuario)
 */
export function resetMensagensService() {
  if (instance) {
    instance.reset();
    instance = null;
  }
}

/**
 * Instancia pre-inicializada para imports rápidos.
 * ATENCIÓN: Debes llamar setUserId() antes de usar si no hay userId en localStorage.
 */
export const mensagensService = getMensagensService();

export default {
  getInstance: getMensagensService,
  reset: resetMensagensService,
  instance: mensagensService,
};